/**
 * /owner/membership/benefits — list grouped by plan, with create/edit/delete.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Award } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, EmptyState, FullSpinner, QueryError, StatusPill,
} from '@/components/ui_primitives/Misc';
import Button from '@/components/ui_primitives/Button';
import Modal from '@/components/ui_primitives/Modal';
import { Input, FieldRow, Textarea } from '@/components/ui_primitives/Field';
import TierBadge from '@/components/membership/TierBadge';

export default function OwnerMembershipBenefitsPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);

  const plansQ = useQuery({
    queryKey: qk.ownerMembershipPlans(brandId),
    queryFn: () => api.ownerMembershipPlans(token, brandId),
    enabled: !!token && !!brandId,
  });

  if (plansQ.isLoading) return <FullSpinner />;
  if (plansQ.isError) return <QueryError error={plansQ.error} onRetry={() => plansQ.refetch()} />;

  const plans = (plansQ.data ?? []).filter((p) => p.active);

  return (
    <div data-testid="owner-membership-benefits-page">
      <PageHeader
        kicker="Memberships"
        title="Benefits"
        subtitle="Per-plan perks that show up in the customer's profile under their active membership."
      />

      {plans.length === 0 ? (
        <SectionCard>
          <EmptyState
            title="No active plans yet"
            subtitle="Create a plan before adding benefits."
            testID="benefits-no-plans"
          />
        </SectionCard>
      ) : (
        <div className="space-y-6">
          {plans.map((p) => (
            <PlanBenefitsCard key={p.id} plan={p} brandId={brandId} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlanBenefitsCard({ plan, brandId }) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const benefitsQ = useQuery({
    queryKey: qk.ownerMembershipBenefits(plan.id),
    queryFn: () => api.ownerMembershipBenefits(token, plan.id),
    enabled: !!token,
  });

  const delM = useMutation({
    mutationFn: (id) => api.ownerMembershipBenefitDelete(token, id),
    onSuccess: () => {
      toast.success('Benefit archived');
      qc.invalidateQueries({ queryKey: qk.ownerMembershipBenefits(plan.id) });
    },
    onError: (e) => toast.error(e?.detail || 'Could not delete benefit'),
  });

  const benefits = (benefitsQ.data ?? []);

  return (
    <SectionCard testID={`benefits-plan-${plan.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-base font-semibold">{plan.name}</h3>
            {plan.tier ? <TierBadge tier={plan.tier} /> : null}
            {plan.is_lifetime ? <StatusPill variant="gold">Lifetime</StatusPill> : null}
          </div>
          <div className="text-xs text-white/45 mt-1">
            {benefits.length} benefit{benefits.length === 1 ? '' : 's'} attached
          </div>
        </div>
        <Button
          variant="secondary" size="sm" leftIcon={<Plus size={12} />}
          onClick={() => setModalOpen(true)}
          testID={`benefit-add-${plan.id}`}
        >
          Add Benefit
        </Button>
      </div>

      {benefits.length === 0 ? (
        <div className="text-sm text-white/45 italic">No benefits yet.</div>
      ) : (
        <ul className="space-y-2" data-testid={`benefit-list-${plan.id}`}>
          {benefits.map((b) => (
            <li
              key={b.id}
              data-testid={`benefit-row-${b.id}`}
              className="flex items-start justify-between gap-3 rounded-xl bg-black/25 border border-white/8 px-4 py-3"
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-gold/15 border border-gold/30 grid place-items-center text-gold shrink-0">
                  <Award size={14} />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm">{b.name}</div>
                  {b.description ? (
                    <div className="text-xs text-white/55 mt-0.5">{b.description}</div>
                  ) : null}
                </div>
              </div>
              <Button
                variant="danger" size="sm" leftIcon={<Trash2 size={12} />}
                loading={delM.isPending && delM.variables === b.id}
                onClick={() => delM.mutate(b.id)}
                testID={`benefit-delete-${b.id}`}
              >
                Archive
              </Button>
            </li>
          ))}
        </ul>
      )}

      <BenefitFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        planId={plan.id}
      />
    </SectionCard>
  );
}

function BenefitFormModal({ open, onClose, planId }) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const m = useMutation({
    mutationFn: () =>
      api.ownerMembershipBenefitCreate(token, planId, {
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Benefit created');
      qc.invalidateQueries({ queryKey: qk.ownerMembershipBenefits(planId) });
      setName(''); setDescription('');
      onClose();
    },
    onError: (e) => toast.error(e?.detail || 'Could not save benefit'),
  });

  return (
    <Modal
      open={open} onClose={onClose}
      title="Add Benefit"
      subtitle="Short, customer-facing description of the perk."
      size="md"
      testID="benefit-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={m.isPending}
            testID="benefit-modal-cancel">Cancel</Button>
          <Button
            variant="primary" loading={m.isPending}
            disabled={!name.trim()}
            onClick={() => m.mutate()}
            testID="benefit-modal-save"
          >
            Create benefit
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <FieldRow label="Name" required>
          <Input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Free Drink Upgrade"
            data-testid="benefit-modal-name"
          />
        </FieldRow>
        <FieldRow label="Description">
          <Textarea
            value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Visible in the customer's profile."
            data-testid="benefit-modal-description"
          />
        </FieldRow>
      </div>
    </Modal>
  );
}
